import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import i18n, { getAppText } from 'src/i18n';
import { showMessage } from '../../components/components/SnackbarNotification/snackbarNotificationSlice';
import { DepartmentDto } from './departmentSlice';

export interface GroupDto {
    [key: string]: any;
    id: number;
    nameFr: string;
    nameEn: string;
    groupId: string;
    businessCategory_Id?: number;
    referenceYear_Id: number;
    office_Id?: number;
    region_Id?: number;
    groupType_Id?: number;
    departments: DepartmentDto[];
}
export interface EditGroupDto {
    [key: string]: any;
    id: number;
    nameFr: string;
    nameEn: string;
    groupId: string;
    businessCategory_Id?: number;
    referenceYear_Id: number;
    office_Id?: number;
    groupType_Id?: number;
    departmentIds: number[];
    usedDepartmentIds: number[];
}

const blankGroupDto: EditGroupDto = {
    id: 0,
    nameFr: '',
    nameEn: '',
    groupId: '',
    referenceYear_Id: 0,
    departmentIds: [],
    usedDepartmentIds: [],
};

export interface GetByDepartmentDto {
    departmentId: number;
    referenceYearId: number;
}
export interface GroupState {
    groups: GroupDto[];
    group: EditGroupDto;
    gettingGroups: boolean;
    gettingGroup: boolean;
    savingGroup: boolean;
    gettingUsedDepartments: boolean;
    archivingGroup: boolean;
    allUsedDepartmentIds: number[];
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    gettingUsedDepartmentStatus: 'idle' | 'loading' | 'succeeded' | 'failed';

    error?: string;
}

const initialState: GroupState = {
    groups: [],
    group: blankGroupDto,
    gettingGroups: false,
    gettingGroup: false,
    savingGroup: false,
    archivingGroup: false,
    gettingUsedDepartments: false,
    status: 'idle',
    gettingUsedDepartmentStatus: 'idle',
    error: undefined,
    allUsedDepartmentIds: [],
};

export const groupSlice = createSlice({
    name: 'groups',
    initialState,
    reducers: {
        clearGroups: (state) => {
            state.groups = [];
            state.group = blankGroupDto;
        },
        updateGroup: (state, param) => {
            const { payload } = param;
            state.group = payload;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(getGroups.pending, (state, action) => {
                state.gettingGroups = true;
                state.status = 'loading';
            })
            .addCase(getGroups.fulfilled, (state, action) => {
                state.gettingGroups = false;
                state.groups = action.payload;
                state.status = 'succeeded';
            })
            .addCase(getGroups.rejected, (state, action) => {
                state.gettingGroups = false;
                state.groups = [];
                state.status = 'failed';
                state.error = action.error.message;
            })
            .addCase(getGroup.pending, (state, action) => {
                state.gettingGroup = true;
                state.status = 'loading';
            })
            .addCase(getGroup.fulfilled, (state, action) => {
                state.gettingGroup = false;
                state.group = action.payload;
                state.status = 'succeeded';
            })
            .addCase(getGroup.rejected, (state, action) => {
                state.gettingGroup = false;
                state.group = blankGroupDto;
                state.status = 'failed';
                state.error = action.error.message;
            })
            .addCase(saveGroup.pending, (state, action) => {
                state.savingGroup = true;
                state.status = 'loading';
            })
            .addCase(saveGroup.fulfilled, (state, action) => {
                state.savingGroup = false;
                state.status = 'succeeded';
            })
            .addCase(saveGroup.rejected, (state, action) => {
                state.savingGroup = false;
                state.status = 'failed';
                state.error = action.error.message;
            })
            .addCase(archiveGroup.pending, (state, action) => {
                state.archivingGroup = true;
                state.status = 'loading';
            })
            .addCase(archiveGroup.fulfilled, (state, action) => {
                state.archivingGroup = false;
                state.groups = state.groups.filter((x) => x.id !== action.meta.arg);
                state.status = 'succeeded';
            })
            .addCase(archiveGroup.rejected, (state, action) => {
                state.archivingGroup = false;
                state.status = 'failed';
                state.error = action.error.message;
            })
            .addCase(getAllUsedDepartments.pending, (state, action) => {
                state.gettingUsedDepartments = true;
                state.gettingUsedDepartmentStatus = 'loading';
            })
            .addCase(getAllUsedDepartments.fulfilled, (state, action) => {
                state.gettingUsedDepartments = false;
                state.allUsedDepartmentIds = action.payload;
                state.gettingUsedDepartmentStatus = 'succeeded';
            })
            .addCase(getAllUsedDepartments.rejected, (state, action) => {
                state.gettingUsedDepartments = false;
                state.error = action.error.message;
                state.gettingUsedDepartmentStatus = 'failed';
            });
    },
});

export const { clearGroups } = groupSlice.actions;
export const { updateGroup } = groupSlice.actions;

export const getGroups = createAsyncThunk('groups/getGroups', async (referenceYearId: number, thunkAPI) => {
    const response = await axios.get(`/api/synergy/groups/getGroups/${referenceYearId}`);

    return response.data;
});

export const getGroup = createAsyncThunk('groups/getGroup', async (id: number, thunkAPI) => {
    const response = await axios.get(`/api/synergy/groups/${id}`);

    return response.data;
});

export const getAllUsedDepartments = createAsyncThunk('groups/getAllUsedDepartments', async (referenceYear_Id: number, thunkAPI) => {
    const response = await axios.get(`/api/synergy/groups/get-all-used-departments/${referenceYear_Id}`);

    return response.data;
});

export const archiveGroup = createAsyncThunk('groups/archiveGroup', async (id: number, thunkAPI) => {
    return axios
        .post(`/api/synergy/groups/archive/${id}`)
        .then((data) => {
            thunkAPI.dispatch(
                showMessage({
                    type: 'info',
                    message: getAppText('archivedGroup'),
                })
            );
            return data;
        })
        .catch(async (error) => {
            const message = await error.response.data.text();
            thunkAPI.dispatch(
                showMessage({
                    type: 'error',
                    message: i18n.t(message),
                })
            );
        });
});

export const saveGroup = createAsyncThunk('groups/saveGroup', async (groupToSave: EditGroupDto, thunkAPI) => {
    if (groupToSave.id === 0) {
        return axios
            .post(`/api/synergy/groups/create`, groupToSave)
            .then((data) => {
                thunkAPI.dispatch(
                    showMessage({
                        type: 'info',
                        message: getAppText('createGroup'),
                    })
                );
                return data;
            })
            .catch(async (error) => {
                const message = await error.response.data.text();
                thunkAPI.dispatch(
                    showMessage({
                        type: 'error',
                        message: i18n.t(message),
                    })
                );
            });
    } else {
        return axios
            .put(`/api/synergy/groups/put`, groupToSave)
            .then((data) => {
                thunkAPI.dispatch(
                    showMessage({
                        type: 'info',
                        message: getAppText('saveGroup'),
                    })
                );
                return data;
            })
            .catch(async (error) => {
                const message = await error.response.data.text();
                thunkAPI.dispatch(
                    showMessage({
                        type: 'error',
                        message: i18n.t(message),
                    })
                );
            });
    }
});
